<template>
  <div class="auth-page">
    <!-- Brand panel (on small screens only the brand row stays, as a top bar). -->
    <div class="auth-brand">
      <div class="brand-row">
        <img src="../../assets/logoK9corp.png" alt="Corporación K-9" class="brand-logo" />
        <div>
          <div class="brand-name">Portal del Candidato</div>
          <div class="brand-sub">Corporación K-9 · Reclutamiento</div>
        </div>
      </div>

      <div class="brand-intro">
        <div class="brand-eyebrow">Trabaje con nosotros</div>
        <h1 class="brand-headline">Su solicitud, en un solo lugar y siempre al día.</h1>
        <p class="brand-text">
          Aplique una vez, dele seguimiento a su proceso y actualice sus documentos cuando los renueve.
        </p>
        <ol class="brand-steps">
          <li v-for="(s, idx) in brandSteps" :key="s.title" class="brand-step">
            <span class="brand-step-num" :class="{ filled: idx === 0 }">{{ idx + 1 }}</span>
            <span>
              <span class="brand-step-title">{{ s.title }}</span>
              <span class="brand-step-text">{{ s.text }}</span>
            </span>
          </li>
        </ol>
      </div>
    </div>

    <div class="auth-main">
      <div class="auth-card">
        <div v-if="notice" class="auth-notice">{{ notice }}</div>

        <!-- STEP 1: the identification decides the next step (log in, create the account, or claim an old application). -->
        <form v-if="mode === 'identify'" key="identify" @submit.prevent="submitIdentify">
          <div class="auth-eyebrow">Paso 1 de 2 · Identificación</div>
          <h2 class="auth-title">Ingrese su identificación</h2>
          <p class="auth-subtitle">Si ya tiene un expediente le pediremos su contraseña. Si no, crearemos su cuenta.</p>

          <div id="id-type-label" class="auth-label">Tipo de identificación</div>
          <div class="id-types" role="radiogroup" aria-labelledby="id-type-label">
            <label v-for="kind in idKinds" :key="kind.value" class="id-type">
              <input
                type="radio"
                name="id-type"
                :value="kind.value"
                :checked="idForm.type === kind.value"
                @change="setIdType(kind.value)"
              />
              <span>{{ kind.label }}</span>
            </label>
          </div>

          <label class="auth-label" for="gov-id">Número de identificación</label>
          <input
            v-if="idKind.mask"
            id="gov-id"
            key="masked"
            v-mask="idKind.mask"
            type="text"
            class="auth-input"
            :value="idForm.number"
            inputmode="numeric"
            autocomplete="username"
            :placeholder="idKind.placeholder"
            @input="idForm.number = $event.target.value"
          />
          <input
            v-else
            id="gov-id"
            key="plain"
            type="text"
            class="auth-input id-upper"
            :value="idForm.number"
            maxlength="20"
            autocomplete="username"
            autocapitalize="characters"
            spellcheck="false"
            :placeholder="idKind.placeholder"
            @input="idForm.number = $event.target.value"
          />

          <div v-if="error" class="auth-error">{{ error }}</div>

          <button type="submit" class="auth-btn" :disabled="loading || !idValid">
            {{ loading ? "Buscando…" : "Continuar" }}
          </button>
        </form>

        <!-- STEP 2: it already has an account -->
        <form v-else-if="mode === 'login'" key="login" @submit.prevent="submitLogin">
          <div class="auth-eyebrow">Paso 2 de 2 · Ingresar</div>
          <h2 class="auth-title">{{ greetingName ? `Bienvenido de nuevo, ${greetingName}` : "Bienvenido de nuevo" }}</h2>
          <p class="auth-subtitle">Ingrese su contraseña para ver o actualizar su expediente.</p>

          <IdentityBox :value="displayId" @change="changeId" />

          <label class="auth-label">Contraseña</label>
          <PasswordField v-model="loginForm.password" autocomplete="current-password" />

          <div v-if="error" class="auth-error">{{ error }}</div>

          <button type="submit" class="auth-btn" :disabled="loading">
            {{ loading ? "Ingresando…" : "Ingresar" }}
          </button>

          <div class="auth-links">
            <button type="button" class="link-btn" @click="switchMode('forgot')">¿Olvidó su contraseña?</button>
          </div>
        </form>

        <!-- STEP 2: nothing under this identification yet -->
        <form v-else-if="mode === 'register'" key="register" @submit.prevent="submitRegister">
          <div class="auth-eyebrow">Paso 2 de 2 · Registro</div>
          <h2 class="auth-title">Cree su cuenta</h2>
          <p class="auth-subtitle">
            No encontramos un expediente con {{ identityKind.noun }}. Complete estos datos para empezar.
          </p>

          <IdentityBox :value="displayId" @change="changeId" />

          <!-- The name: from the padrón when it has this cédula (then it can't be changed), otherwise typed. -->
          <div class="name-grid">
            <div v-for="field in nameFields" :key="field.key" :class="{ 'name-wide': field.wide }">
              <label class="auth-label" :for="'reg-' + field.key">{{ field.label }}</label>
              <input
                :id="'reg-' + field.key"
                v-model.trim="registerForm[field.key]"
                type="text"
                class="auth-input"
                :class="{ 'is-locked': nameLocks[field.key] }"
                :readonly="nameLocks[field.key]"
                :tabindex="nameLocks[field.key] ? -1 : null"
                :autocomplete="field.autocomplete"
                spellcheck="false"
                required
              />
            </div>
          </div>
          <div v-if="nameFromRegistry" class="auth-hint">Tomado del padrón del Registro Civil.</div>

          <label class="auth-label">Correo electrónico</label>
          <input
            type="email"
            class="auth-input"
            v-model.trim="registerForm.email"
            autocomplete="email"
            placeholder="nombre@correo.com"
            required
          />

          <label class="auth-label">Contraseña</label>
          <PasswordField v-model="registerForm.password" autocomplete="new-password" :minlength="8" />
          <div class="auth-hint">Mínimo 8 caracteres.</div>

          <div v-if="error" class="auth-error">{{ error }}</div>

          <button type="submit" class="auth-btn" :disabled="loading">
            {{ loading ? "Creando cuenta…" : "Crear cuenta" }}
          </button>
        </form>

        <!-- STEP 2: an application from before accounts existed; a code to its email proves it's theirs. -->
        <form v-else-if="mode === 'claim'" key="claim" @submit.prevent="submitClaimCode()">
          <div class="auth-eyebrow">Paso 2 de 2 · Verificación</div>
          <h2 class="auth-title">{{ greetingName ? `Encontramos su solicitud, ${greetingName}` : "Encontramos su solicitud" }}</h2>
          <p v-if="identity.emailHint" class="auth-subtitle">
            Ya tiene una solicitud con {{ identityKind.noun }}, de antes de que existieran las cuentas. Para activar su
            cuenta le enviaremos un código a <strong>{{ identity.emailHint }}</strong>.
          </p>
          <p v-else class="auth-subtitle">
            Ya tiene una solicitud con {{ identityKind.noun }}, pero no tiene un correo al que podamos enviarle el código
            para activar su cuenta. Contacte al equipo de Recursos Humanos de K-9.
          </p>

          <IdentityBox :value="displayId" @change="changeId" />

          <div v-if="error" class="auth-error">{{ error }}</div>

          <button v-if="identity.emailHint" type="submit" class="auth-btn" :disabled="loading">
            {{ loading ? "Enviando…" : "Enviar código" }}
          </button>

          <div class="auth-links">
            <button type="button" class="link-btn" :disabled="loading" @click="haveClaimCode">Ya tengo un código</button>
          </div>
        </form>

        <form v-else-if="mode === 'claim-confirm'" key="claim-confirm" @submit.prevent="submitClaimConfirm">
          <div class="auth-eyebrow">Paso 2 de 2 · Verificación</div>
          <h2 class="auth-title">Active su cuenta</h2>
          <p v-if="identity.emailHint" class="auth-subtitle">
            Escriba el código que enviamos a <strong>{{ identity.emailHint }}</strong> y cree su contraseña.
          </p>
          <p v-else class="auth-subtitle">Escriba el código que le enviamos a su correo y cree su contraseña.</p>

          <IdentityBox :value="displayId" @change="changeId" />

          <label class="auth-label">Código de verificación</label>
          <input
            type="text"
            class="auth-input"
            v-model.trim="claimForm.code"
            inputmode="numeric"
            autocomplete="one-time-code"
            required
          />

          <label class="auth-label">Contraseña</label>
          <PasswordField v-model="claimForm.password" autocomplete="new-password" :minlength="8" />
          <div class="auth-hint">Mínimo 8 caracteres.</div>

          <div v-if="error" class="auth-error">{{ error }}</div>

          <button type="submit" class="auth-btn" :disabled="loading">
            {{ loading ? "Activando…" : "Activar cuenta" }}
          </button>

          <div class="auth-links">
            <button type="button" class="link-btn" :disabled="loading" @click="submitClaimCode({ resend: true })">
              Enviar otro código
            </button>
          </div>
        </form>

        <!-- FORGOT PASSWORD: the code goes to the email of the identified account -->
        <form v-else-if="mode === 'forgot'" key="forgot" @submit.prevent="submitForgot">
          <div class="auth-eyebrow">Recuperar contraseña</div>
          <h2 class="auth-title">¿Olvidó su contraseña?</h2>
          <p class="auth-subtitle">Le enviaremos un código al correo registrado en su cuenta para que cree una nueva.</p>

          <IdentityBox :value="displayId" @change="changeId" />

          <div v-if="error" class="auth-error">{{ error }}</div>

          <button type="submit" class="auth-btn" :disabled="loading">
            {{ loading ? "Enviando…" : "Enviar código" }}
          </button>

          <div class="auth-links">
            <button type="button" class="link-btn" :disabled="loading" @click="haveForgotCode">Ya tengo un código</button>
            <button type="button" class="link-btn" @click="switchMode('login')">Volver a iniciar sesión</button>
          </div>
        </form>

        <!-- FORGOT PASSWORD: enter code + new password -->
        <form v-else-if="mode === 'reset'" key="reset" @submit.prevent="submitReset">
          <div class="auth-eyebrow">Recuperar contraseña</div>
          <h2 class="auth-title">Restablecer contraseña</h2>
          <p v-if="identity.emailHint" class="auth-subtitle">
            Revise su correo <strong>{{ identity.emailHint }}</strong> e ingrese el código recibido.
          </p>
          <p v-else class="auth-subtitle">Revise el correo registrado en su cuenta e ingrese el código recibido.</p>

          <IdentityBox :value="displayId" @change="changeId" />

          <label class="auth-label">Código de verificación</label>
          <input
            type="text"
            class="auth-input"
            v-model.trim="resetForm.code"
            inputmode="numeric"
            autocomplete="one-time-code"
            :readonly="resetCodeVerified"
            required
          />

          <!-- The new password is asked for only once the code has been checked. -->
          <template v-if="resetCodeVerified">
            <label class="auth-label">Nueva contraseña</label>
            <PasswordField v-model="resetForm.password" autocomplete="new-password" :minlength="8" />

            <label class="auth-label">Confirmar nueva contraseña</label>
            <PasswordField v-model="resetForm.passwordConfirmation" autocomplete="new-password" :minlength="8" />
          </template>

          <div v-if="error" class="auth-error">{{ error }}</div>

          <button type="submit" class="auth-btn" :disabled="loading">
            <template v-if="resetCodeVerified">{{ loading ? "Guardando…" : "Restablecer contraseña" }}</template>
            <template v-else>{{ loading ? "Verificando…" : "Verificar código" }}</template>
          </button>

          <div class="auth-links">
            <button v-if="resetCodeVerified" type="button" class="link-btn" :disabled="loading" @click="changeResetCode">
              Cambiar código
            </button>
            <button v-else type="button" class="link-btn" @click="switchMode('forgot')">Solicitar otro código</button>
            <button type="button" class="link-btn" @click="switchMode('login')">Volver a iniciar sesión</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import * as authApi from "../../services/authApi";
import { firstName } from "../portal/candidateSummary";
import IdentityBox from "./IdentityBox.vue";
import PasswordField from "./PasswordField.vue";

const BRAND_STEPS = [
  { title: "Ingrese su identificación", text: "Con ella entra a su cuenta o la crea." },
  { title: "Complete su solicitud en 4 pasos", text: "Se guarda sola; puede terminarla después." },
  { title: "Dele seguimiento", text: "Vea el estado y reciba avisos de Reclutamiento." },
];

// Step 1's identification types, in the design's order (values as in constants/id-types.js). The number
// is sent the way the backend stores it: cédula física and DIMEX as digits only, a passport in uppercase.
const ID_KINDS = [
  {
    value: 1,
    label: "Cédula física",
    name: "cédula física",
    noun: "esta cédula",
    mask: "#-####-####",
    placeholder: "0-0000-0000",
    pattern: /^[1-9]\d{8}$/,
  },
  {
    value: 3,
    label: "Pasaporte",
    name: "pasaporte",
    noun: "este pasaporte",
    mask: "",
    placeholder: "Número de pasaporte",
    pattern: /^[0-9A-Z]{6,20}$/,
  },
  {
    value: 2,
    label: "DIMEX",
    name: "DIMEX",
    noun: "este DIMEX",
    mask: "############",
    placeholder: "11 o 12 dígitos",
    pattern: /^\d{11,12}$/,
  },
];

// "Cree su cuenta" asks for the name as the portal's "Datos personales" did.
const NAME_FIELDS = [
  { key: "firstName", label: "Nombre", autocomplete: "given-name", wide: true },
  { key: "lastName", label: "Primer apellido", autocomplete: "family-name" },
  { key: "surName", label: "Segundo apellido", autocomplete: "off" },
];

function emptyRegisterForm() {
  return { firstName: "", lastName: "", surName: "", email: "", password: "" };
}

// Where each /auth/identify status leads.
const IDENTIFY_NEXT_MODE = { registered: "login", unclaimed: "claim", new: "register" };

const PASSWORD_REJECTED =
  "Elija otra contraseña: debe tener al menos 8 caracteres, no ser solo números ni una contraseña muy común.";
const CODE_REJECTED = "El código no es válido o ya venció. Revíselo o solicite otro.";
// Claiming an old application whose email already belongs to an account that can't take it over.
const CLAIM_EMAIL_TAKEN =
  "El correo de esta solicitud ya pertenece a otra cuenta. Contacte al equipo de Recursos Humanos de K-9 para activarla.";

function kindOf(type) {
  return ID_KINDS.find((kind) => kind.value === type) || ID_KINDS[0];
}

function normalizeGovId(kind, number) {
  const text = String(number || "");
  return kind.mask ? text.replace(/\D/g, "") : text.replace(/[^0-9a-z]/gi, "").toUpperCase();
}

export default {
  name: "AuthGate",
  components: { IdentityBox, PasswordField },
  props: {
    // Shown once on load (e.g. "your session expired") — App.vue passes
    // this after a forced logout; ignored otherwise.
    initialNotice: { type: String, default: "" },
  },
  data() {
    return {
      brandSteps: BRAND_STEPS,
      idKinds: ID_KINDS,
      // identify, then: login (it has an account) | register (nothing yet) | claim → claim-confirm (an
      // application from before accounts existed). forgot → reset recovers the identified account's password.
      mode: "identify",
      loading: false,
      error: "",
      notice: this.initialNotice,
      idForm: { type: 1, number: "" },
      // The identification confirmed in step 1, and what /auth/identify said about it.
      identity: { govId: "", type: 1, firstName: "", emailHint: null },
      loginForm: { password: "" },
      registerForm: emptyRegisterForm(),
      nameFields: NAME_FIELDS,
      // The parts of the name the padrón filled in, which can't be changed.
      nameLocks: { firstName: false, lastName: false, surName: false },
      claimForm: { code: "", password: "" },
      resetForm: { code: "", password: "", passwordConfirmation: "" },
      // The reset's code has been checked: only then are the new password's fields shown.
      resetCodeVerified: false,
    };
  },
  computed: {
    idKind() {
      return kindOf(this.idForm.type);
    },
    govId() {
      return normalizeGovId(this.idKind, this.idForm.number);
    },
    idValid() {
      return this.idKind.pattern.test(this.govId);
    },
    identityKind() {
      return kindOf(this.identity.type);
    },
    displayId() {
      const id = this.identity.govId;
      return this.identity.type === 1 ? `${id.slice(0, 1)}-${id.slice(1, 5)}-${id.slice(5)}` : id;
    },
    greetingName() {
      return firstName({ first_name: this.identity.firstName });
    },
    nameFromRegistry() {
      return Object.values(this.nameLocks).some(Boolean);
    },
  },
  watch: {
    // Each step starts on its first field (none on the claim step, which only has a button).
    mode() {
      this.focusFirstField();
    },
    // Checking the reset's code moves to the password (and changing it, back to the code).
    resetCodeVerified() {
      this.focusFirstField();
    },
  },
  methods: {
    focusFirstField() {
      this.$nextTick(() => {
        const field = this.$el.querySelector(".auth-card form input:not([hidden]):not([type='radio']):not([readonly])");
        if (field) field.focus();
      });
    },
    // The reset starts over from its code.
    clearReset() {
      this.resetForm = { code: "", password: "", passwordConfirmation: "" };
      this.resetCodeVerified = false;
    },
    // Back to the code, to type another (the passwords typed so far are dropped).
    changeResetCode() {
      this.resetForm = { code: this.resetForm.code, password: "", passwordConfirmation: "" };
      this.resetCodeVerified = false;
      this.error = "";
    },
    switchMode(mode) {
      this.mode = mode;
      this.error = "";
      this.notice = "";
    },
    // The applicant already has a code (Recruitment sent it, or an earlier request did): straight to typing it, without
    // sending another. The backend accepts any code of theirs that hasn't expired.
    haveForgotCode() {
      this.clearReset();
      this.switchMode("reset");
    },
    haveClaimCode() {
      this.claimForm = { code: "", password: "" };
      this.switchMode("claim-confirm");
    },
    setIdType(type) {
      // A number typed for another type doesn't carry over (the wizard does the same).
      this.idForm = { type, number: "" };
      this.error = "";
    },
    // Back to step 1 with the identification still filled in; whatever step 2 had is dropped.
    changeId() {
      this.loginForm = { password: "" };
      this.registerForm = emptyRegisterForm();
      this.nameLocks = { firstName: false, lastName: false, surName: false };
      this.claimForm = { code: "", password: "" };
      this.clearReset();
      this.switchMode("identify");
    },
    errorMessage(err) {
      const status = err && err.response && err.response.status;
      if (status === 429) return "Demasiados intentos. Espere un momento e intente de nuevo.";
      if (status && status < 500) return "Datos incorrectos.";
      return "Ocurrió un error. Intente de nuevo.";
    },
    // The 400 body of a rejected request, or null.
    badRequest(err) {
      const response = err && err.response;
      return response && response.status === 400 ? response.data || {} : null;
    },
    isEmailTaken(err) {
      const data = this.badRequest(err);
      const emailErrors = data && data.email;
      return Array.isArray(emailErrors) && emailErrors.includes("email already registered");
    },
    // Django's password validators (length, common, all digits, similar to the email).
    isPasswordRejected(err, key = "password") {
      const data = this.badRequest(err);
      return !!(data && data[key]);
    },
    typeMismatchMessage(stored, chosen) {
      return `Este número está registrado como ${stored.name}, no como ${chosen.name}. Seleccione «${stored.label}» para continuar.`;
    },
    // A request that has to be redone from step 1: the identification changed state in between.
    restartMessage() {
      const noun = this.identityKind.noun;
      return `${noun.charAt(0).toUpperCase()}${noun.slice(1)} ya tiene una cuenta. Presione «Cambiar» y continúe de nuevo para ingresar.`;
    },
    submitIdentify() {
      if (!this.idValid || this.loading) return;
      this.error = "";
      this.notice = "";
      this.loading = true;
      const govId = this.govId;
      const type = this.idForm.type;
      authApi
        .identify(govId)
        .then((response) => {
          const data = response.data || {};
          const next = IDENTIFY_NEXT_MODE[data.status];
          if (!next) {
            this.error = this.errorMessage();
            return null;
          }
          // The number belongs to an account or an application with another type of identification: it isn't theirs
          // to enter with this one. (No type in the answer, or an unknown one, doesn't block.)
          const stored = ID_KINDS.find((kind) => kind.value === Number(data.identification_type_id));
          if (next !== "register" && stored && stored.value !== type) {
            this.error = this.typeMismatchMessage(stored, kindOf(type));
            return null;
          }
          // A new cédula física: the padrón may already have the name "Cree su cuenta" asks for.
          const person = next === "register" && type === 1 ? this.findPerson(govId) : Promise.resolve(null);
          return person.then((found) => {
            this.identity = { govId, type, firstName: data.first_name || "", emailHint: data.email_hint || null };
            if (next === "register") this.startRegister(found);
            this.mode = next;
          });
        })
        .catch((err) => {
          const data = this.badRequest(err);
          this.error = data && data.gov_id ? "Revise el número de identificación." : this.errorMessage(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // The padrón's { first_name, last_name, sur_name } for that cédula, or null (not there, or it couldn't be asked).
    findPerson(govId) {
      return authApi
        .lookupPerson(govId)
        .then((response) => response.data || null)
        .catch(() => null);
    },
    // Fills in (and locks) what the padrón had; the rest is typed.
    startRegister(person) {
      const name = (key) => String((person && person[key]) || "").trim();
      const form = emptyRegisterForm();
      form.firstName = name("first_name");
      form.lastName = name("last_name");
      form.surName = name("sur_name");
      this.registerForm = form;
      this.nameLocks = { firstName: !!form.firstName, lastName: !!form.lastName, surName: !!form.surName };
    },
    submitLogin() {
      this.error = "";
      this.notice = "";
      this.loading = true;
      authApi
        .login(this.identity.govId, this.loginForm.password)
        .then((response) => {
          this.$emit("authenticated", response.data);
        })
        .catch((err) => {
          const status = err && err.response && err.response.status;
          if (status === 401) this.error = "Contraseña incorrecta.";
          else if (status === 404) this.error = "No encontramos una cuenta con esta identificación.";
          else this.error = this.errorMessage(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitRegister() {
      this.error = "";
      const { firstName, lastName, surName, email, password } = this.registerForm;
      if (!firstName || !lastName || !surName) {
        this.error = "Escriba su nombre y sus dos apellidos.";
        return;
      }
      this.loading = true;
      authApi
        .register({
          govId: this.identity.govId,
          identificationTypeId: this.identity.type,
          firstName,
          lastName,
          surName,
          email,
          password,
        })
        .then((response) => {
          this.$emit("authenticated", response.data);
        })
        .catch((err) => {
          const data = this.badRequest(err);
          if (data && data.gov_id) {
            this.error = this.restartMessage();
          } else if (data && (data.first_name || data.last_name || data.sur_name)) {
            this.error = "Revise su nombre y sus apellidos.";
          } else if (this.isEmailTaken(err)) {
            // Also what an account from before identifications gets with a wrong password (see authApi.register()).
            this.error = "Este correo ya está registrado en el sistema.";
          } else if (this.isPasswordRejected(err)) {
            this.error = PASSWORD_REJECTED;
          } else {
            this.error = this.errorMessage(err);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitClaimCode({ resend = false } = {}) {
      this.error = "";
      this.notice = "";
      this.loading = true;
      authApi
        .sendClaimCode(this.identity.govId)
        .then((response) => {
          const hint = response.data && response.data.email_hint;
          if (hint) this.identity.emailHint = hint;
          this.claimForm.code = "";
          this.mode = "claim-confirm";
          if (resend) this.notice = "Le enviamos un código nuevo.";
        })
        .catch((err) => {
          const data = this.badRequest(err);
          const status = err && err.response && err.response.status;
          if (data && data.no_email) {
            // Back to the claim step, which explains there's no email to send it to.
            this.identity.emailHint = null;
            this.mode = "claim";
          } else if (data && data.email_taken) {
            this.error = CLAIM_EMAIL_TAKEN;
          } else if (status === 404) {
            this.error = this.restartMessage();
          } else {
            this.error = this.errorMessage(err);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitClaimConfirm() {
      this.error = "";
      this.notice = "";
      this.loading = true;
      authApi
        .confirmClaim(this.identity.govId, this.claimForm.code, this.claimForm.password)
        .then((response) => {
          this.$emit("authenticated", response.data);
        })
        .catch((err) => {
          const data = this.badRequest(err);
          if (data && data.email_taken) {
            this.error = CLAIM_EMAIL_TAKEN;
          } else if (this.isPasswordRejected(err)) {
            this.error = PASSWORD_REJECTED;
          } else if (data) {
            this.error = CODE_REJECTED;
          } else {
            this.error = this.errorMessage(err);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitForgot() {
      this.error = "";
      this.loading = true;
      authApi
        .forgotPassword(this.identity.govId)
        .then((response) => {
          this.identity.emailHint = (response.data && response.data.email_hint) || null;
          this.clearReset();
          this.notice = "";
          this.mode = "reset";
        })
        .catch((err) => {
          this.error = this.errorMessage(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // First step of the reset: only the code is sent, to see it is good before the new password is asked for.
    submitVerifyCode() {
      this.error = "";
      this.loading = true;
      authApi
        .verifyResetCode(this.identity.govId, this.resetForm.code)
        .then(() => {
          this.resetCodeVerified = true;
        })
        .catch((err) => {
          this.error = this.badRequest(err) ? CODE_REJECTED : this.errorMessage(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitReset() {
      if (!this.resetCodeVerified) {
        this.submitVerifyCode();
        return;
      }
      this.error = "";
      if (this.resetForm.password !== this.resetForm.passwordConfirmation) {
        this.error = "Las contraseñas no coinciden.";
        return;
      }
      this.loading = true;
      authApi
        .resetPassword(
          this.identity.govId,
          this.resetForm.code,
          this.resetForm.password,
          this.resetForm.passwordConfirmation
        )
        .then(() => {
          this.loginForm.password = "";
          this.mode = "login";
          this.error = "";
          this.notice = "Contraseña actualizada. Ingrese con su nueva contraseña.";
        })
        .catch((err) => {
          if (this.isPasswordRejected(err, "new_password")) {
            this.error = PASSWORD_REJECTED;
          } else if (this.badRequest(err)) {
            // The code stopped working since it was checked (it expired): back to the code.
            this.changeResetCode();
            this.error = CODE_REJECTED;
          } else {
            this.error = this.errorMessage(err);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  /* White, like the form: it is what shows through the brand panel's rounded corners. */
  background: #fff;
}

/* ---- Brand panel ---- */
.auth-brand {
  position: relative;
  overflow: hidden;
  /* Rounded on the side that faces the form (the right one; the bottom one on small screens). */
  border-radius: 0 32px 32px 0;
  /* 45% + 150px, but always leaving the form at least 480px. */
  flex: 0 0 min(calc(45% + 150px), calc(100% - 480px));
  max-width: 830px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 56px clamp(32px, 5.5vw, 80px);
  /* Same dark navy with a soft glow in the upper right as the portal's banner (see CandidatePortal). */
  background: radial-gradient(1200px 400px at 80% -10%, #1c2a4a 0%, transparent 60%), #0a1020;
}
.brand-row {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 14px;
}
.brand-logo {
  flex-shrink: 0;
  height: 52px;
  width: auto;
  display: block;
}
.brand-name {
  font: 700 20px/1.2 "IBM Plex Sans", sans-serif;
  letter-spacing: -0.01em;
  color: #fff;
}
.brand-sub {
  margin-top: 3px;
  font: 12px "IBM Plex Sans", sans-serif;
  color: #c0c4d3;
}
.brand-intro {
  position: relative;
  z-index: 1;
  max-width: 560px;
  margin-top: clamp(48px, 11vh, 110px);
}
.brand-eyebrow {
  font: 700 12.5px "IBM Plex Sans", sans-serif;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #caa651;
}
.brand-headline {
  margin: 18px 0 0;
  font: 700 clamp(32px, 3.2vw, 44px) / 1.15 "IBM Plex Sans", sans-serif;
  letter-spacing: -0.018em;
  color: #fff;
}
.brand-text {
  margin: 18px 0 0;
  font: 16px/1.55 "IBM Plex Sans", sans-serif;
  color: #d4d7e2;
}
.brand-steps {
  list-style: none;
  margin: 44px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.brand-step {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.brand-step-num {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #caa651;
  border-radius: 50%;
  font: 700 14px "IBM Plex Sans", sans-serif;
  color: #caa651;
}
.brand-step-num.filled {
  background: #caa651;
  color: #222a47;
}
.brand-step-title {
  display: block;
  font: 600 15.5px "IBM Plex Sans", sans-serif;
  color: #fff;
}
.brand-step-text {
  display: block;
  margin-top: 3px;
  font: 13.5px "IBM Plex Sans", sans-serif;
  color: #c0c4d3;
}

/* ---- Forms ---- */
.auth-main {
  flex: 1;
  box-sizing: border-box;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
}
.auth-card {
  width: 100%;
  max-width: 430px;
}
.auth-notice {
  margin-bottom: 20px;
  padding: 10px 14px;
  border-radius: 999px;
  background: #e6f7ec;
  color: #1c6b3a;
  font: 12.5px "IBM Plex Sans", sans-serif;
  text-align: center;
}
/* The brand panel's gold, darkened enough to read as text on white. */
.auth-eyebrow {
  margin-bottom: 14px;
  font: 700 12px "IBM Plex Sans", sans-serif;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8a6720;
}
.auth-title {
  margin: 0;
  font: 700 30px/1.2 "IBM Plex Sans", sans-serif;
  letter-spacing: -0.015em;
  color: #222a47;
}
.auth-subtitle {
  margin: 8px 0 40px;
  font: 15px "IBM Plex Sans", sans-serif;
  color: #5b6070;
}
.auth-label {
  display: block;
  margin: 22px 0 8px;
  font: 600 13.5px "IBM Plex Sans", sans-serif;
  color: #222a47;
}
.auth-input {
  box-sizing: border-box;
  width: 100%;
  height: 52px;
  padding: 0 16px;
  border: 1px solid #d5d9e0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font: 15px "IBM Plex Sans", sans-serif;
  color: #1c2434;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.auth-input::placeholder {
  color: #9aa1ae;
}
.auth-input:hover {
  border-color: #b9bfcb;
}
.auth-input:focus {
  outline: none;
  border-color: #c9963a;
  box-shadow: 0 0 0 4px rgba(201, 150, 58, 0.18);
}
/* Passports: shown in uppercase as typed (sent uppercased too), but not the placeholder. */
.id-upper {
  text-transform: uppercase;
}
.id-upper::placeholder {
  text-transform: none;
}
.id-types {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 28px;
  min-height: 44px;
  align-items: center;
}
.id-type {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font: 15px "IBM Plex Sans", sans-serif;
  color: #222a47;
  cursor: pointer;
}
.id-type input {
  width: 20px;
  height: 20px;
  margin: 0;
  accent-color: #222a47;
  cursor: pointer;
}
/* The name on its own row, the two surnames side by side. */
.name-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 12px;
}
.name-wide {
  grid-column: 1 / -1;
}
/* From the padrón: shown, not edited (the same look as the identification above). */
.auth-input.is-locked,
.auth-input.is-locked:hover,
.auth-input.is-locked:focus {
  border-color: #d5d9e0;
  background: #f6f7f9;
  box-shadow: none;
  cursor: default;
}
.auth-hint {
  margin-top: 8px;
  font: 13px "IBM Plex Sans", sans-serif;
  color: #5b6070;
}
.auth-error {
  margin-top: 14px;
  font: 13px "IBM Plex Sans", sans-serif;
  color: #b3261e;
}
.auth-btn {
  width: 100%;
  height: 52px;
  margin-top: 24px;
  border: none;
  border-radius: 12px;
  background: #222a47;
  color: #fff;
  font: 600 16px "IBM Plex Sans", sans-serif;
  cursor: pointer;
}
.auth-btn:hover:not(:disabled) {
  background: #1a2139;
}
.auth-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.auth-links {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.link-btn {
  width: 100%;
  height: 48px;
  box-sizing: border-box;
  border: 1px solid #d5d9e0;
  border-radius: 12px;
  background: #fff;
  font: 600 15px "IBM Plex Sans", sans-serif;
  color: #222a47;
  cursor: pointer;
}
.link-btn:hover:not(:disabled) {
  background: #f6f7f9;
}
.link-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Small screens: the brand panel goes first, compacted, and the form follows it. */
@media (max-width: 900px) {
  .auth-page {
    flex-direction: column;
  }
  .auth-brand {
    flex: none;
    max-width: none;
    padding: 24px 20px 28px;
    border-radius: 0 0 15px 15px;
  }
  .brand-intro {
    max-width: none;
    margin-top: 24px;
  }
  .brand-eyebrow {
    font-size: 11px;
  }
  .brand-headline {
    margin-top: 10px;
    font-size: 26px;
  }
  .brand-text {
    margin: 10px 0 0;
    font-size: 14px;
  }
  /* The three steps are for the wide layout, where the brand panel has the room: on a phone the banner stops at the text. */
  .brand-steps {
    display: none;
  }
  .auth-main {
    align-items: flex-start;
    padding: 28px 20px 40px;
  }
  /* Keeps the three identification types on one row on phones. */
  .id-types {
    column-gap: 18px;
  }
  .id-type {
    gap: 8px;
  }
}
</style>
